import { useLocation } from '@tanstack/react-router'
import {
  LayoutDashboard,
  FolderKanban,
  ListTodo,
  Clock,
  Users,
  UsersRound,
  Building2,
  Briefcase,
  CalendarDays,
  Banknote,
  Wallet,
  Settings,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Network,
  BookOpen,
  ClipboardList,
  BarChart3,
  Monitor,
  Laptop,
  User,
  UserCircle,
  Receipt,
  Bell,
  UserCog,
  GitBranch,
  CalendarOff,
  ClipboardCheck,
  BarChart2,
  DollarSign,
  FileText,
  Folder,
  Timer,
  Landmark,
  ArrowLeftRight,
  TrendingUp,
  Tags,
  Building,
  Cog,
  BellRing,
  FileSearch,
  Workflow,
  Zap,
  PieChart,
  Coins,
} from 'lucide-react'
import { useState, useCallback } from 'react'

import { Badge } from '@/components/ui/badge'
import { useRoleNavigation, type NavItemWithBadge } from '@/hooks'
import { cn } from '@/lib/utils'
import { useUIStore } from '@/stores/ui.store'

const ICONS: Record<string, typeof LayoutDashboard> = {
  LayoutDashboard,
  FolderKanban,
  ListTodo,
  Clock,
  Users,
  UsersRound,
  Building2,
  Briefcase,
  CalendarDays,
  Banknote,
  Wallet,
  Settings,
  Network,
  BookOpen,
  ClipboardList,
  BarChart3,
  Monitor,
  Laptop,
  User,
  UserCircle,
  Receipt,
  Bell,
  UserCog,
  GitBranch,
  CalendarOff,
  ClipboardCheck,
  BarChart2,
  DollarSign,
  FileText,
  Folder,
  Timer,
  Landmark,
  ArrowLeftRight,
  TrendingUp,
  Tags,
  Building,
  Cog,
  BellRing,
  FileSearch,
  Workflow,
  Zap,
  PieChart,
  Coins,
}

interface NavItemProps {
  item: NavItemWithBadge
  level: number
  collapsed: boolean
  expandedIds: Set<string>
  onToggle: (id: string) => void
  currentPath: string
}

function isPathActive(itemPath: string | undefined, currentPath: string): boolean {
  if (!itemPath) {
    return false
  }
  if (itemPath === '/dashboard') {
    return currentPath === '/dashboard'
  }
  return currentPath.startsWith(itemPath)
}

function isItemOrChildActive(item: NavItemWithBadge, currentPath: string): boolean {
  if (isPathActive(item.path, currentPath)) {
    return true
  }
  if (item.children) {
    return item.children.some((child) => isItemOrChildActive(child, currentPath))
  }
  return false
}

function NavItemComponent({
  item,
  level,
  collapsed,
  expandedIds,
  onToggle,
  currentPath,
}: NavItemProps) {
  const Icon = item.icon ? ICONS[item.icon] : null
  const hasChildren = item.children && item.children.length > 0
  const isExpanded = expandedIds.has(item.id)
  const isActive = isPathActive(item.path, currentPath)
  const isChildActive = hasChildren && isItemOrChildActive(item, currentPath)
  const showBadge = item.badgeCount !== undefined && item.badgeCount > 0
  const paddingLeft = collapsed ? 12 : 12 + level * 12

  if (hasChildren) {
    return (
      <li>
        <button
          onClick={() => onToggle(item.id)}
          className={cn(
            'flex w-full items-center gap-3 rounded-md py-2 text-sm font-medium transition-colors',
            isChildActive
              ? 'bg-accent text-accent-foreground'
              : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
          )}
          style={{ paddingLeft, paddingRight: 12 }}
          title={collapsed ? item.label : undefined}
        >
          {Icon && <Icon className="h-5 w-5 shrink-0" />}
          {!collapsed && (
            <>
              <span className="flex-1 text-left">{item.label}</span>
              <ChevronDown
                className={cn('h-4 w-4 shrink-0 transition-transform', isExpanded && 'rotate-180')}
              />
            </>
          )}
        </button>
        {!collapsed && isExpanded && (
          <ul className="mt-1 space-y-1">
            {item.children?.map((child) => (
              <NavItemComponent
                key={child.id}
                item={child}
                level={level + 1}
                collapsed={collapsed}
                expandedIds={expandedIds}
                onToggle={onToggle}
                currentPath={currentPath}
              />
            ))}
          </ul>
        )}
      </li>
    )
  }

  return (
    <li>
      <a
        href={item.path}
        className={cn(
          'flex items-center gap-3 rounded-md py-2 text-sm font-medium transition-colors',
          isActive
            ? 'bg-primary text-primary-foreground'
            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
        )}
        style={{ paddingLeft, paddingRight: 12 }}
        title={collapsed ? item.label : undefined}
      >
        {Icon && <Icon className="h-5 w-5 shrink-0" />}
        {!collapsed && (
          <>
            <span className="flex-1">{item.label}</span>
            {showBadge && (
              <Badge
                variant={isActive ? 'secondary' : 'default'}
                className="ml-auto h-5 min-w-5 justify-center px-1.5"
              >
                {(item.badgeCount ?? 0) > 99 ? '99+' : item.badgeCount}
              </Badge>
            )}
          </>
        )}
        {collapsed && showBadge && (
          <span className="bg-primary absolute right-1 top-1 h-2 w-2 rounded-full" />
        )}
      </a>
    </li>
  )
}

export function Sidebar() {
  const location = useLocation()
  const sidebarCollapsed = useUIStore((state) => state.sidebarCollapsed)
  const toggleSidebar = useUIStore((state) => state.toggleSidebar)
  const { items: navItems } = useRoleNavigation()

  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => {
    const expanded = new Set<string>()
    const findAndExpand = (items: NavItemWithBadge[], path: string[]) => {
      for (const item of items) {
        if (isItemOrChildActive(item, location.pathname)) {
          path.forEach((id) => expanded.add(id))
          expanded.add(item.id)
        }
        if (item.children) {
          findAndExpand(item.children, [...path, item.id])
        }
      }
    }
    findAndExpand(navItems, [])
    return expanded
  })

  const handleToggle = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }, [])

  return (
    <aside
      className={cn(
        'bg-card fixed left-0 top-0 z-40 flex h-screen flex-col border-r transition-all duration-300',
        sidebarCollapsed ? 'w-16' : 'w-64'
      )}
    >
      <div className="flex h-16 items-center justify-between border-b px-4">
        {!sidebarCollapsed && <span className="text-lg font-semibold">管理系统</span>}
        <button
          onClick={toggleSidebar}
          className="hover:bg-accent rounded-md p-1.5"
          aria-label={sidebarCollapsed ? '展开侧边栏' : '折叠侧边栏'}
        >
          {sidebarCollapsed ? (
            <ChevronRight className="h-5 w-5" />
          ) : (
            <ChevronLeft className="h-5 w-5" />
          )}
        </button>
      </div>
      <nav className="flex-1 overflow-y-auto p-2">
        <ul className="space-y-1">
          {navItems.map((item) => (
            <NavItemComponent
              key={item.id}
              item={item}
              level={0}
              collapsed={sidebarCollapsed}
              expandedIds={expandedIds}
              onToggle={handleToggle}
              currentPath={location.pathname}
            />
          ))}
        </ul>
      </nav>
    </aside>
  )
}
